import { useState } from "react";
import { useLocation, useHistory } from "react-router-dom";

import AudioSeriesForm from "./AudioSeriesForm";
import { useRequestProcessor } from "../../../hooks/useRequestProcessors";
import { getKey, MODULE_KEYS } from "../../../api/api.keys";
import { GetAudioById, UpdateAlbum } from "../../../api/api.service";
import { hasData } from "../../../utils/commonUtils";
import { LoaderFallbackUI } from "../../../components/LoaderFallbackUI";
import SubscriptionStatus, { SUBSCRIPTION_STATUS } from "../../../constants/SubscriptionStatus";
import { finalizeMediaUtil, initializeMediaUtil, uploadOnS3 } from "../../../utils/mediaUtils";
import { APP_ROUTES } from "../../../helpers/routeHelpers";
import { showToast, SUCCESS } from "../../../utils/toastUtils";

const UpdateSeries = () => {
    const location = useLocation();
    const history = useHistory();
    const [isMediaUpload, setIsMediaUpload] = useState(false);

    // Extract the ID from the path (assuming the path is "/audio/singles/details/21")
    const seriesID = location.pathname.split("/").pop();
    let { useFetch, useMutate } = useRequestProcessor();

    let { mutate, isLoading: isAudioSeriesUpdated } = useMutate("", getKey(), (data) =>
        UpdateAlbum(data, seriesID)
    );

    const { data, isLoading, isFetching } = useFetch(
        getKey([MODULE_KEYS.SERIES_DETAILS_AUDIO_MANAGEMENT], { seriesID }),
        async ({ signal }) =>
            await GetAudioById({
                Id: seriesID
            })
    );

    const updateAudioHandler = async (values) => {
        try {
            setIsMediaUpload(true);
            // Process regular fields
            const uploadResults = await Promise.all(
                Object.keys(values).map(async (fieldName) => {
                    const fieldData = values[fieldName];
                    if (fieldData?.file && fieldData?.exactFile) {
                        const mediaId = await handleMediaUpload(
                            fieldData?.file,
                            fieldData?.exactFile
                        );
                        return { fieldName, mediaId };
                    }
                    return null;
                })
            );

            // Attach mediaIds to form values
            uploadResults.forEach((result) => {
                if (result) {
                    const { fieldName, mediaId } = result;
                    values[fieldName].mediaId = mediaId;
                }
            });

            // Process session files
            for (const session of values.sessions) {
                if (session.sessionUploadAudio?.file) {
                    const mediaId = await handleMediaUpload(
                        session.sessionUploadAudio.file,
                        session.sessionUploadAudio.exactFile
                    );
                    session.sessionUploadAudio.mediaId = { id: mediaId };
                }
            }
            const formValues = {
                title: values.title,
                description: values.description,
                authorId: Number(values.author.value),
                categoryId: 2,
                labelIds: values.subCategory?.map((item) => item.value),
                narratorId: Number(values.narrator.value),

                thumbnailId: values.uploadImage?.id
                    ? Number(values.uploadImage?.id)
                    : values.uploadImage?.mediaId?.id,
                croppedThumbnailId: values.uploadThumbnail?.id
                    ? Number(values.uploadThumbnail?.id)
                    : values.uploadThumbnail?.mediaId?.id,
                tierType: SUBSCRIPTION_STATUS.FREEMIUM,
                audios: values.sessions.map((session) => ({
                    title: session.sessionTitle,
                    description: session.sessionDescription,
                    duration: Math.floor(Number(session.sessionUploadAudio?.duration)),
                    audioMediaId: session.sessionUploadAudio?.mediaId?.id?.id
                        ? Number(session.sessionUploadAudio?.mediaId?.id?.id)
                        : Number(session.sessionUploadAudio?.audioMedia?.id),

                    tierType:
                        session.radioGroup ==
                        SubscriptionStatus.getDisplayTextKey(SUBSCRIPTION_STATUS.FREEMIUM)
                            ? SUBSCRIPTION_STATUS.FREEMIUM
                            : SUBSCRIPTION_STATUS.PREMIUM,
                    completionMessage: session?.sessionCompletionmessage,
                    ...(session?.id && { id: session?.id })
                }))
            };

            mutate(formValues, {
                onSuccess: (res) => {
                    showToast(SUCCESS, "Series Audio Updated Successfully!");
                    setIsMediaUpload(false);
                    history.push(APP_ROUTES.AUDIO_CONTENT_MANAGEMENT_SERIES);
                },
                onError: (err) => {
                    setIsMediaUpload(false);
                    console.log("Album not created", err);
                }
            });
        } catch (error) {
            console.error("Error during form submission:", error);
            setIsMediaUpload(false);
        }
    };

    // media upload on server
    const handleMediaUpload = async (file, initFile) => {
        return initializeMediaUtil(file).then(async (res) => {
            const credentials = res;
            await uploadOnS3(initFile, credentials);
            return await finalizeMediaUtil(credentials?.mediaId);
        });
    };

    if (isFetching || isLoading || !hasData(data)) {
        return (
            <div style={{ height: "70vh" }}>
                <LoaderFallbackUI isLoader={isFetching} className={"h-100"} />
            </div>
        );
    }

    return (
        <AudioSeriesForm
            mode="edit"
            isLoading={isMediaUpload || isAudioSeriesUpdated}
            data={data}
            onUpdateAudio={updateAudioHandler}
            isDisabled={isMediaUpload}
        />
    );
};

export default UpdateSeries;
